import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";

const SuperAdminLogin = () => {
  const theme = useTheme();
  const {
    bootstrapSuperAdmin,
    requestEmailVerification,
    verifyEmailCode,
  } = useAuth();

  const [step, setStep] = useState(1); // 1 = email, 2 = code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(true);

  // Wipe any cached session on mount, then bootstrap a public restaurant context.
  useEffect(() => {
    (async () => {
      try {
        localStorage.removeItem("token");
        await bootstrapSuperAdmin();
        setBootstrapped(true);
      } catch (e) {
        setError(e.message || "Could not start super admin login.");
      } finally {
        setBootstrapping(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await requestEmailVerification(email);
      setStep(2);
    } catch (err) {
      setError(err.message || "Failed to send code.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await verifyEmailCode(email, code, "/super-admin");
    } catch (err) {
      setError(err.message || "Invalid code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100svh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #3a5340 100%)`,
        px: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: 4,
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: theme.palette.primary.main,
            color: "white",
            width: 56,
            height: 56,
            borderRadius: "50%",
            mb: 2,
          }}
        >
          <AdminPanelSettingsIcon fontSize="large" />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          Super Admin Login
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {step === 1
            ? "Enter your email — we'll send you a 6-digit code."
            : `We sent a code to ${email}.`}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {bootstrapping ? (
          <Box sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !bootstrapped ? (
          <Typography color="error">
            Could not initialise. Make sure at least one restaurant exists.
          </Typography>
        ) : step === 1 ? (
          <Box component="form" onSubmit={handleSendCode}>
            <Stack spacing={2}>
              <TextField
                autoFocus
                fullWidth
                required
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || !email}
                sx={{ height: 48, fontWeight: 700 }}
              >
                {loading ? <CircularProgress size={22} /> : "Send code"}
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleVerify}>
            <Stack spacing={2}>
              <TextField
                autoFocus
                fullWidth
                required
                label="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 6,
                  style: { letterSpacing: "0.5rem", textAlign: "center" },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || code.length < 6}
                sx={{ height: 48, fontWeight: 700 }}
              >
                {loading ? <CircularProgress size={22} /> : "Verify & sign in"}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setStep(1);
                  setCode("");
                }}
                sx={{ textTransform: "none" }}
              >
                Use a different email
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SuperAdminLogin;
